import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useCallback } from "react";
import { useAuth } from "./useAuth";

interface WishlistState {
  handles: string[];
  loading: boolean;
  _userId: string | null;
}

const useWishlistStore = create<WishlistState>(() => ({
  handles: [],
  loading: false,
  _userId: null,
}));

function setStoreState(partial: Partial<WishlistState>) {
  useWishlistStore.setState(partial);
}

export function useWishlist() {
  const { user, loading: authLoading } = useAuth();
  const { handles, loading } = useWishlistStore(useShallow((s) => ({ handles: s.handles, loading: s.loading })));

  // Sync user and fetch wishlist when user changes
  useEffect(() => {
    const uid = user?.id ?? null;
    const current = useWishlistStore.getState();
    if (uid === current._userId) return;
    setStoreState({ _userId: uid });

    if (!uid) {
      setStoreState({ handles: [], loading: false });
      return;
    }

    let cancelled = false;
    const fetchData = async () => {
      setStoreState({ loading: true });
      try {
        const { data, error } = await supabase
          .from("wishlist")
          .select("product_handle")
          .eq("user_id", uid)
          .order("created_at", { ascending: false });
        if (error) throw error;
        if (!cancelled) {
          setStoreState({
            handles: (data || []).map((r: { product_handle: string }) => r.product_handle),
            loading: false,
          });
        }
      } catch (err) {
        console.error("Failed to fetch wishlist:", err);
        if (!cancelled) setStoreState({ loading: false });
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [user?.id]);

  const toggle = useCallback(async (handle: string) => {
    if (!user) return false;
    const current = useWishlistStore.getState().handles;
    const isSaved = current.includes(handle);
    try {
      if (isSaved) {
        setStoreState({ handles: current.filter((h) => h !== handle) });
        await supabase.from("wishlist").delete().eq("user_id", user.id).eq("product_handle", handle);
      } else {
        setStoreState({ handles: [handle, ...current] });
        await supabase.from("wishlist").insert({ user_id: user.id, product_handle: handle });
      }
      return true;
    } catch (err) {
      console.error("Failed to toggle wishlist:", err);
      setStoreState({ handles: current });
      return false;
    }
  }, [user]);

  const isWishlisted = useCallback((handle: string) => handles.includes(handle), [handles]);

  return { handles, loading, authLoading, toggle, isWishlisted, isLoggedIn: !!user };
}
